import { Trans } from "react-i18next";
import React, { useState, Suspense, lazy, useEffect, useMemo } from "react";
import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "@shared/styles/accessibility.css"; // Global accessibility styles
import "@shared/styles/app-ui-tokens.css"; // Shared UI typography tokens
import "@shared/styles/app-ui-compact.css"; // Shared compact card/list patterns
import "@shared/styles/app-ui-controls.css"; // Tables, forms, pagination, search
import "@shared/styles/app-ui-header.css"; // Header chrome (dropdowns, search, notifications)
import "@shared/styles/app-ui-navbar.css"; // Left sidebar (MiniNavbar)
import "@shared/styles/app-ui-legal.css"; // Legal pages + footer modal
import "@shared/styles/app-ui-auth.css"; // Login, demo modal, auth footer
import "@shared/styles/enterprise-pages.css"; // Settings / security tab shells
import "@shared/styles/app-ui-page-fit.css"; // Compact admin/analytics page rhythm
import "@shared/styles/app-ui-mobile-layout.css"; // Shared mobile shell, header panels, scroll
import "@shared/styles/performance.css"; // Performance optimizations (font-display, lazy loading)
import "@shared/init/accessibilityInit";
import {
  FeatureGate,
  RegulatoryPhaseGate,
} from "@shared/hooks/useFeatureFlags";
import LockScreen from "@features/UserAccount/components/LockScreen";
const PerformanceMonitorDashboard = lazy(
  () => import("@shared/components/PerformanceMonitorDashboard"),
);
const QuickSearch = lazy(() => import("@shared/components/QuickSearch"));
import logger from "@shared/utils/logger";
import { useSessionLock } from "@features/Security/hooks/useSessionLock";
import { useLockPolicy } from "@shared/hooks/useLockPolicy";
import { useAuth } from "@shared/context/AuthContext";
import { useRoleLookupConfig } from "@shared/hooks/useLookupConfig";
import { canAccessCommercialBilling } from "@shared/utils/roleRegistry";
import RequireAnonymous from "@shared/components/RequireAnonymous";
import QualityWorkspaceNav from "@features/Quality/components/QualityWorkspaceNav";
import AccountHubNav from "@shared/components/AccountHubNav";
import InteroperabilityNav from "@shared/components/InteroperabilityNav";
import { shouldUseWorkspaceLookup } from "@shared/utils/orgLoginRouting";
import {
  isAuthRoutePath,
  writeLastAuthenticatedRoute,
} from "@shared/utils/authRouting";

type ToastContainerComponent = React.ComponentType<{
  position?: string;
  autoClose?: number;
  hideProgressBar?: boolean;
  newestOnTop?: boolean;
  closeOnClick?: boolean;
  rtl?: boolean;
  pauseOnFocusLoss?: boolean;
  draggable?: boolean;
  pauseOnHover?: boolean;
  theme?: string;
  "aria-live"?: string;
  role?: string;
}>;

/** Keep react-toastify (vendor-ui) off the entry static graph for /worklist LCP. */
function DeferredToastContainer(
  props: React.ComponentProps<ToastContainerComponent>,
) {
  const [ToastContainer, setToastContainer] =
    useState<ToastContainerComponent | null>(null);

  useEffect(() => {
    let cancelled = false;
    const mount = () => {
      void import("react-toastify").then((module) => {
        if (cancelled) return;
        setToastContainer(() => module.ToastContainer as ToastContainerComponent);
      });
    };
    const ric = (
      window as Window & {
        requestIdleCallback?: (
          cb: IdleRequestCallback,
          opts?: IdleRequestOptions,
        ) => number;
      }
    ).requestIdleCallback;
    const handle =
      typeof ric === "function"
        ? ric(() => mount(), { timeout: 2500 })
        : window.setTimeout(mount, 1);
    return () => {
      cancelled = true;
      if (typeof ric === "function") {
        (
          window as Window & { cancelIdleCallback?: (id: number) => void }
        ).cancelIdleCallback?.(handle as number);
      } else {
        window.clearTimeout(handle as number);
      }
    };
  }, []);

  if (!ToastContainer) return null;
  return <ToastContainer {...props} />;
}
let deferredFrameworkStylesLoaded = false;
let nonCriticalAppStylesLoaded = false;

function loadDeferredFrameworkStyles(): void {
  if (deferredFrameworkStylesLoaded) {
    return;
  }

  deferredFrameworkStylesLoaded = true;
  void Promise.allSettled([
    import("bootstrap-icons/font/bootstrap-icons.css"),
  ]);
}

function loadNonCriticalAppStyles(): void {
  if (nonCriticalAppStylesLoaded) {
    return;
  }

  nonCriticalAppStylesLoaded = true;
  void Promise.allSettled([
    import("react-toastify/dist/ReactToastify.css"),
    import("@shared/styles/permissionToast.css"),
    import("@shared/styles/theme-transitions.css"),
  ]);
}

// Critical imports - loaded immediately for fast initial render
const Dashboard = lazy(() => import("@shared/components/dashboard"));
const LoginPage = lazy(() => import("@features/UserAccount/pages/LoginPage"));
const WorkspaceLookupPage = lazy(
  () => import("@features/UserAccount/pages/WorkspaceLookupPage"),
);
const ForgotWorkspacePage = lazy(
  () => import("@features/UserAccount/pages/ForgotWorkspacePage"),
);
const ForgotPasswordPage = lazy(
  () => import("@features/UserAccount/pages/forgotPasswordPage"),
);
const AcceptInvitationPage = lazy(
  () => import("@features/UserAccount/pages/AcceptInvitationPage"),
);
const NotFoundPage = lazy(() => import("@shared/components/NotFoundPage"));
const FeatureDeprecationNotice = lazy(
  () => import("@shared/components/FeatureDeprecationNotice"),
);

// Lazy-loaded route components - Code split for optimal performance (75% bundle reduction)
const PatientManagement = lazy(
  () => import("@features/Patient/components/PatientManagement"),
);
const NewPatientPage = lazy(
  () => import("@features/Patient/pages/NewPatientPage"),
);
const PatientDetailPage = lazy(
  () => import("@features/Patient/pages/PatientDetailPage"),
);
const SessionManagementPage = lazy(
  () => import("@features/UserAccount/pages/SessionManagementPage"),
);
const ResetPasswordPage = lazy(
  () => import("@features/UserAccount/pages/resetPasswordPage"),
);
const NewAppointmentPage = lazy(
  () => import("@features/AppointmentScheduling/pages/NewAppointmentPage"),
);
const AppointmentTabsPage = lazy(
  () => import("@features/AppointmentScheduling/pages/AppointmentTabsPage"),
);
const NewReportPage = lazy(
  () => import("@features/Report/pages/NewReportPage"),
);
const ReportDetailPage = lazy(
  () => import("@features/Report/pages/ReportDetailPage"),
);
const WorklistPage = lazy(
  () => import("@features/Worklist/pages/WorklistPage"),
);
const StudiesPage = lazy(() => import("@features/study/pages/StudiesPage"));
const StudyDetailPage = lazy(
  () => import("@features/study/pages/StudyDetailPage"),
);
const NotificationsPage = lazy(
  () => import("@features/NotificationsAndAlerts/pages/NotificationsPage"),
);
const MobileNotificationInboxPage = lazy(
  () =>
    import("@features/NotificationsAndAlerts/pages/MobileNotificationInboxPage"),
);
const TelehealthPage = lazy(
  () => import("@features/Telehealth/pages/TelehealthPage"),
);
const SignalQualityMonitoringPage = lazy(
  () => import("@features/Quality/pages/SignalQualityMonitoringPage"),
);
const AnalyticsDashboard = lazy(
  () => import("@features/ReportsAndAnalytics/AnalyticsDashboard"),
);
const ReportsAndAnalyticsPage = lazy(
  () => import("@features/ReportsAndAnalytics/ReportsAndAnalytics"),
);
const AdvancedAnalyticsPage = lazy(
  () => import("@features/ReportsAndAnalytics/AdvancedAnalyticsPage"),
);
const PresetsPage = lazy(() => import("@features/Presets/pages/PresetsPage"));
const MontagesPage = lazy(() => import("@features/Montage/pages/MontagesPage"));
const MontageEditor = lazy(
  () => import("@features/Montage/components/MontageEditor"),
);
const ModernPerspectivesPage = lazy(
  () => import("@features/Perspective/pages/ModernPerspectivesPage"),
);
const PerspectiveFormPage = lazy(
  () => import("@features/Perspective/pages/PerspectiveFormPage"),
);
const PerspectiveAdminPanel = lazy(
  () => import("@features/Perspective/components/PerspectiveAdminPanel"),
);
const PerspectiveHelpPanel = lazy(
  () => import("@features/Perspective/components/PerspectiveHelpPanel"),
);
const AccountSecurityPage = lazy(
  () => import("@features/SecurityAndPrivacy/pages/AccountSecurityPage"),
);
const AdminSecurityPage = lazy(
  () =>
    import("@features/SecurityAndPrivacy/pages/SecurityAndPrivacyPageModern"),
);
const SettingsPage = lazy(
  () => import("@features/Settings/pages/SettingsPageModern"),
);
const ICUMonitoringDashboard = lazy(
  () => import("@features/ReportsAndAnalytics/ICUMonitoringDashboard"),
);
const IcuCeegWorkbenchPanel = lazy(
  () => import("@features/EEG/components/IcuCeegWorkbenchPanel"),
);
const MonitoringWorkbenchPage = lazy(
  () => import("@features/EEG/pages/MonitoringWorkbenchPage"),
);
const ImagingCoReviewPanel = lazy(
  () => import("@features/EEG/components/ImagingCoReviewPanel"),
);
const ReportProductivityPanel = lazy(
  () => import("@features/Report/components/ReportProductivityPanel"),
);
const Visualization3DPage = lazy(
  () => import("@features/Visualization/pages/Visualization3DPage"),
);
const UserManagementPage = lazy(
  () => import("@features/UserAccount/pages/UserManagementPageModern"),
);
const UserDetailsPage = lazy(
  () => import("@features/UserAccount/pages/userDetailsPage"),
);
const OrganizationSettingsPage = lazy(
  () => import("@features/Admin/pages/OrganizationSettingsPage"), // REG-011
);
const EHRConnectionsPage = lazy(
  () => import("@features/Admin/pages/EHRConnectionsPage"),
);
const EHRSyncActivityPage = lazy(
  () => import("@features/Admin/pages/EHRSyncActivityPage"),
);
const ICDCodeManagementPage = lazy(
  () => import("@features/Admin/pages/ICDCodeManagementPage"),
);
const AnnotationTypeManagementPage = lazy(
  () => import("@features/Admin/pages/AnnotationTypeManagementPage"),
);
const PriorityWorkflowManagementPage = lazy(
  () => import("@features/Admin/pages/PriorityWorkflowManagementPage"),
);
const NotificationAdminPage = lazy(
  () => import("@features/Admin/pages/NotificationAdminPage"),
);
const DocumentTemplateLibraryPage = lazy(
  () => import("@features/DocumentTemplates/pages/DocumentTemplateLibraryPage"),
);
const HelpPage = lazy(() => import("@shared/components/HelpPage"));
const UserGuidePage = lazy(
  () => import("@features/Help/pages/UserGuidePage"),
);
const MySupportTicketsPage = lazy(
  () => import("@features/Help/pages/MySupportTicketsPage"),
);
const SupportConversationPage = lazy(
  () => import("@features/Help/pages/SupportConversationPage"),
);
const TrainingPortal = lazy(
  () => import("@features/Training/pages/TrainingPortal"),
);
const ClinicalEnablementPanel = lazy(
  () => import("@features/Training/components/ClinicalEnablementPanel"),
);
const SourceLocalizationPanel = lazy(
  () => import("@features/EEG/components/SourceLocalizationPanel"),
);
const CentralMonitoringWallPanel = lazy(
  () => import("@features/EEG/components/CentralMonitoringWallPanel"),
);
const VendorSupportMatrixPanel = lazy(
  () => import("@features/EEG/components/VendorSupportMatrixPanel"),
);
const TermsOfServicePage = lazy(
  () => import("@shared/pages/TermsOfServicePage"),
);
const PrivacyPolicyPage = lazy(() => import("@shared/pages/PrivacyPolicyPage"));
const HipaaNoticePage = lazy(() => import("@shared/pages/HipaaNoticePage"));
const DataSecurityPage = lazy(() => import("@shared/pages/DataSecurityPage"));
const CompliancePage = lazy(() => import("@shared/pages/CompliancePage"));
const ApiReferencePage = lazy(() => import("@shared/pages/ApiReferencePage"));
const ReleaseNotesPage = lazy(() => import("@shared/pages/ReleaseNotesPage"));
const EEGViewerPage = lazy(() => import("@features/EEG/pages/EEGViewerPage"));
import EEGPlayableShell from "@features/EEG/components/EEGPlayableShell";
const ViewerRedirectPage = lazy(
  () => import("@features/EEG/pages/ViewerRedirectPage"),
);

const enableGlobalEEGRoutePrefetch =
  import.meta.env.VITE_EEG_GLOBAL_ROUTE_PREFETCH === "true";

// Optional global idle prefetch for study-detail → EEG navigation. Disabled by
// default so direct /eeg/* Lighthouse runs do not download the viewer graph
// during the critical startup window. Intent prefetch uses hover/focus handlers
// on study/worklist/patient links instead.
if (
  enableGlobalEEGRoutePrefetch &&
  typeof window !== "undefined" &&
  !window.location.pathname.startsWith("/eeg/")
) {
  const prefetchEEGViewerPage = () =>
    void import("@features/EEG/pages/EEGViewerPage");

  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(prefetchEEGViewerPage, { timeout: 5000 });
  } else {
    window.setTimeout(prefetchEEGViewerPage, 5000);
  }
}
const VideoPopoutPage = lazy(
  () => import("@features/VideoEEG/pages/VideoPopoutPage"),
);
const CustomizationHubPage = lazy(
  () => import("@features/EEG/pages/CustomizationHubPage"),
);
const QADashboard = lazy(() => import("@features/Quality/pages/QADashboard"));
const SharesPage = lazy(() => import("@features/Sharing/pages/SharesPage"));
const CollaborationWorkspacePage = lazy(
  () => import("@features/Collaboration/pages/CollaborationWorkspacePage"),
);
const BillingManagementPage = lazy(
  () => import("@features/Billing/BillingManagementPage"),
);
const OrgCommercialInvoicePage = lazy(
  () => import("@features/Billing/OrgCommercialInvoicePage"),
);

// Sprint 5: Compliance Dashboard - HIPAA compliance reporting
const ComplianceDashboard = lazy(
  () => import("@features/Compliance/pages/ComplianceDashboard"),
);

// Sprint 1: Public Sharing - SharedResourcePage for public access without authentication
const SharedResourcePage = lazy(
  () => import("@features/Sharing/pages/SharedResourcePage"),
);

// Orphaned dashboards — now routed and accessible via sidebar
const AnonymizationDashboard = lazy(
  () => import("@features/Anonymization/components/AnonymizationDashboard"),
);
const IntegrityDashboard = lazy(
  () => import("@features/Audit/components/IntegrityDashboard"),
);
const ImportExportDashboard = lazy(
  () => import("@features/FormatConversion/components/ImportExportDashboard"),
);
const CodingDashboard = lazy(
  () => import("@features/MedicalCoding/components/CodingDashboard"),
);
const MedicalCodingWorkspacePage = lazy(
  () => import("@features/MedicalCoding/pages/MedicalCodingWorkspacePage"),
);
const ConsensusDashboard = lazy(
  () => import("@features/Quality/pages/ConsensusDashboard"),
);

// Mount global share modal (bootstrapped UI for perspective sharing)
import { ensureShareModalMounted } from "@shared/init/shareModalBootstrap";

import RequireAuth from "@shared/components/RequireAuth";
import ErrorBoundary from "@shared/components/ErrorBoundary";
import GlobalErrorBoundary from "@shared/components/GlobalErrorBoundary";
import MFAModal from "@shared/components/MFAModal";
import RequireModernBrowser from "@shared/components/RequireModernBrowser";
import KeyboardShortcuts, {
  ShortcutHelpPanel,
  ShortcutConfig,
} from "@shared/components/KeyboardShortcuts";
import { UserPreferencesProvider } from "@shared/context/UserPreferencesContext";
import { StudyProvider } from "@shared/context/StudyContext";
import { usePermissionHandler } from "@shared/hooks/usePermissionHandler";
import { MontageProvider } from "@shared/context/MontageContext";
import { PerspectiveStateProvider } from "@shared/context/PerspectiveStateContext";
import { OrganizationProvider } from "@shared/context/OrganizationContext";
import { ThemeProvider } from "@shared/contexts/ThemeProvider";
import { SettingsProvider } from "@shared/contexts/SettingsContext";
import { AppDialogProvider } from "@shared/context/AppDialogContext";
import SettingsDomApplier from "@shared/components/SettingsDomApplier";

/** Skip Study/Montage/Perspective on /worklist — not needed for LCP. */
function ClinicalProviders({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const skipHeavyClinical =
    pathname === "/worklist" || pathname.startsWith("/worklist/");
  if (skipHeavyClinical) {
    return <OrganizationProvider>{children}</OrganizationProvider>;
  }
  return (
    <StudyProvider>
      <OrganizationProvider>
        <MontageProvider>
          <PerspectiveStateProvider>{children}</PerspectiveStateProvider>
        </MontageProvider>
      </OrganizationProvider>
    </StudyProvider>
  );
}const MainLayout = lazy(() => import("@shared/components/layouts/MainLayout"));
const AuthLayout = lazy(() => import("@shared/components/layouts/AuthLayout"));
const EEGLayout = lazy(() => import("@shared/components/layouts/EEGLayout"));
const LegalLayout = lazy(
  () => import("@shared/components/layouts/LegalLayout"),
);

type AppShellSpinnerProps = {
  animation?: string;
  className?: string;
  size?: string;
};

const Spinner: React.FC<AppShellSpinnerProps> = ({ className, size }) => {
  const dimension = size === "sm" ? "1rem" : "2rem";

  return (
    <div className={className} role="status" aria-live="polite">
      <div
        aria-hidden="true"
        style={{
          width: dimension,
          height: dimension,
          margin: "0 auto",
          borderRadius: "9999px",
          border: "0.2rem solid currentColor",
          borderTopColor: "transparent",
          opacity: 0.75,
        }}
      />
      <span className="visually-hidden"><Trans ns="features" i18nKey="ui.copy.8f26c6520d61" /></span>
    </div>
  );
};

const BillingManageRedirect: React.FC = () => {
  const location = useLocation();
  const nextParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const targetPath = useMemo(() => {
    const legacyTab = nextParams.get("tab");
    nextParams.delete("tab");

    switch (legacyTab) {
      case "payments":
        return "/admin/billing/payments";
      case "commercial-state":
        return "/admin/billing/plan";
      case "seat-assignments":
        return "/admin/billing/seats";
      default:
        return "/admin/billing/seats";
    }
  }, [nextParams]);
  const targetSearch = nextParams.toString();

  return (
    <Navigate
      to={{
        pathname: targetPath,
        search: targetSearch ? `?${targetSearch}` : "",
      }}
      replace
    />
  );
};

const RequireCommercialBillingAccess: React.FC<{
  children: React.ReactElement;
}> = ({ children }) => {
  const location = useLocation();
  const { user, loading } = useAuth();
  const roleLookups = useRoleLookupConfig();

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!canAccessCommercialBilling(user?.role, roleLookups)) {
    const from = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={
          from.includes("request-license=1")
            ? `/dashboard?request-license=1&from=${encodeURIComponent(from)}`
            : "/dashboard"
        }
        replace
      />
    );
  }

  return children;
};

const CONTROL_PLANE_UI_BASE_URL = (
  import.meta.env.VITE_CONTROL_PLANE_UI_URL || "http://nervedx.localhost:5174"
).replace(/\/+$/, "");

function buildControlPlaneTarget(path: string): string {
  if (!path.startsWith("/")) {
    return `${CONTROL_PLANE_UI_BASE_URL}/${path}`;
  }
  return `${CONTROL_PLANE_UI_BASE_URL}${path}`;
}

const ControlPlaneRouteRedirect: React.FC<{
  featureName: string;
  targetPath: string;
}> = ({ featureName, targetPath }) => {
  const target = useMemo(
    () => buildControlPlaneTarget(targetPath),
    [targetPath],
  );

  useEffect(() => {
    window.location.assign(target);
  }, [target]);

  return (
    <div className="container py-4">
      <h2>{featureName} <Trans ns="features" i18nKey="ui.copy.ffb1dff489e1" /></h2>
      <p className="text-muted mb-3">
        <Trans ns="features" i18nKey="ui.copy.3b3c6b18750c" /></p>
      <a className="btn btn-primary" href={target}>
        <Trans ns="features" i18nKey="ui.copy.b5b1e153860e" /></a>
    </div>
  );
};

const App: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    loadDeferredFrameworkStyles();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (typeof window.requestIdleCallback === "function") {
      const idleHandle = window.requestIdleCallback(
        () => {
          loadNonCriticalAppStyles();
        },
        { timeout: 2000 },
      );

      return () => {
        window.cancelIdleCallback(idleHandle);
      };
    }

    const timeoutHandle = window.setTimeout(() => {
      loadNonCriticalAppStyles();
    }, 1200);

    return () => {
      window.clearTimeout(timeoutHandle);
    };
  }, []);
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { policy } = useLockPolicy();
  const { isLocked, autoLocked, lockSession, unlockSession } = useSessionLock({
    inactivityTimeout: policy.inactivityTimeoutMinutes,
    enabled: true,
    enableKeyboardShortcut: false,
  });

  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      return;
    }

    if (isAuthRoutePath(location.pathname)) {
      return;
    }

    writeLastAuthenticatedRoute(
      `${location.pathname}${location.search}${location.hash}`,
    );
  }, [
    authLoading,
    isAuthenticated,
    location.hash,
    location.pathname,
    location.search,
  ]);

  useEffect(() => {
    // Second createRoot + ShareModal (vendor-ui) is not LCP-critical.
    const ric = (
      window as Window & {
        requestIdleCallback?: (
          cb: IdleRequestCallback,
          opts?: IdleRequestOptions,
        ) => number;
      }
    ).requestIdleCallback;
    const handle =
      typeof ric === "function"
        ? ric(() => ensureShareModalMounted(), { timeout: 4000 })
        : window.setTimeout(() => ensureShareModalMounted(), 1);
    return () => {
      if (typeof ric === "function") {
        (
          window as Window & { cancelIdleCallback?: (id: number) => void }
        ).cancelIdleCallback?.(handle as number);
      } else {
        window.clearTimeout(handle as number);
      }
    };
  }, []);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showQuickSearch, setShowQuickSearch] = useState(false);
  const routeShellFallback = (
    <div className="text-center p-5">
      <Spinner animation="border" />
    </div>
  );
  const forceDirectLogin =
    new URLSearchParams(window.location.search).get("direct") === "1";
  const loginElement = (
    <Suspense fallback={routeShellFallback}>
      {shouldUseWorkspaceLookup(window.location.hostname, forceDirectLogin) ? (
        <WorkspaceLookupPage />
      ) : (
        <LoginPage />
      )}
    </Suspense>
  );

  // Centralized permission error handling with toast notifications
  usePermissionHandler({
    enabled: true,
    onPermissionDenied: (detail) => {
      // Optional: Send to analytics or audit log service
      logger.info("Permission audit event", {
        timestamp: new Date().toISOString(),
        resource: detail.url,
        action: detail.method,
      });
    },
  });

  // Keyboard shortcuts configuration
  const shortcuts: ShortcutConfig[] = [
    {
      key: "Ctrl+K",
      description: "Open quick search",
      action: () => setShowQuickSearch(true),
      category: "Navigation",
    },
    {
      key: "Ctrl+P",
      description: "Go to patients",
      action: () => navigate("/patients"),
      category: "Navigation",
    },
    {
      key: "Ctrl+R",
      description: "Go to reports",
      action: () => navigate("/reports/review"),
      category: "Navigation",
    },
    {
      key: "Ctrl+H",
      description: "Go to dashboard",
      action: () => navigate("/dashboard"),
      category: "Navigation",
    },
    {
      key: "Ctrl+,",
      description: "Open preferences",
      action: () => navigate("/account/preferences/general"),
      category: "General",
    },
    {
      key: "?",
      description: "Show keyboard shortcuts",
      action: () => setShowShortcuts(true),
      category: "General",
    },
    {
      key: "Ctrl+L",
      description: "Lock screen",
      action: () => lockSession(false),
      category: "General",
    },
  ];

  return (
    <GlobalErrorBoundary>
      <ErrorBoundary>
        <SettingsProvider>
          <AppDialogProvider>
          <ThemeProvider>
            <SettingsDomApplier />
            <UserPreferencesProvider>
              <ClinicalProviders>
                      {/* REG-015: Lock screen overlay - highest z-index to prevent interaction */}
                      {isLocked && (
                        <LockScreen
                          onUnlock={unlockSession}
                          autoLocked={autoLocked}
                        />
                      )}

                      {/* Global MFA modal listens for `app:mfa-required` events */}
                      <MFAModal />
                      {/* Keyboard shortcuts handler - Global */}
                      <KeyboardShortcuts shortcuts={shortcuts} enabled={true} />

                      {/* Toast notifications container for permission errors */}
                      <DeferredToastContainer
                        position="top-right"
                        autoClose={7000}
                        hideProgressBar={false}
                        newestOnTop
                        closeOnClick
                        rtl={false}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                        theme="light"
                        aria-live="assertive"
                        role="alert"
                      />

                      <Routes>
                        <Route
                          path="/"
                          element={<Navigate to="/dashboard" />}
                        />

                        {/* Public Sharing Route - No Authentication Required */}
                        <Route
                          path="/shared/:token"
                          element={
                            <Suspense
                              fallback={
                                <div className="text-center p-5">
                                  <Spinner animation="border" />
                                </div>
                              }
                            >
                              <SharedResourcePage />
                            </Suspense>
                          }
                        />

                        {/* Authentication Routes - Use AuthLayout (no navbar/header) */}
                        <Route
                          element={
                            <RequireModernBrowser>
                              <Suspense fallback={routeShellFallback}>
                                <AuthLayout />
                              </Suspense>
                            </RequireModernBrowser>
                          }
                        >
                          <Route element={<RequireAnonymous />}>
                            <Route path="/login" element={loginElement} />
                          </Route>
                          <Route
                            path="/forgot-password"
                            element={
                              <Suspense fallback={routeShellFallback}>
                                <ForgotPasswordPage />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/reset-password"
                            element={
                              <Suspense
                                fallback={
                                  <div className="text-center p-5">
                                    <Spinner animation="border" />
                                  </div>
                                }
                              >
                                <ResetPasswordPage />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/forgot-workspace"
                            element={
                              <Suspense fallback={routeShellFallback}>
                                <ForgotWorkspacePage />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/accept-invitation"
                            element={
                              <Suspense fallback={routeShellFallback}>
                                <AcceptInvitationPage />
                              </Suspense>
                            }
                          />
                        </Route>
                        <Route
                          path="/control/*"
                          element={
                            <RequireModernBrowser>
                              <Navigate to="/login" replace />
                            </RequireModernBrowser>
                          }
                        />

                        {/* Legal & Documentation Routes - Use LegalLayout (branded top bar + footer) */}
                        <Route
                          element={
                            <Suspense fallback={routeShellFallback}>
                              <LegalLayout />
                            </Suspense>
                          }
                        >
                          <Route
                            path="/terms-of-service"
                            element={
                              <Suspense
                                fallback={
                                  <div className="text-center p-5">
                                    <Spinner animation="border" />
                                  </div>
                                }
                              >
                                <TermsOfServicePage />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/privacy-policy"
                            element={
                              <Suspense
                                fallback={
                                  <div className="text-center p-5">
                                    <Spinner animation="border" />
                                  </div>
                                }
                              >
                                <PrivacyPolicyPage />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/hipaa-notice"
                            element={
                              <Suspense
                                fallback={
                                  <div className="text-center p-5">
                                    <Spinner animation="border" />
                                  </div>
                                }
                              >
                                <HipaaNoticePage />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/data-security"
                            element={
                              <Suspense
                                fallback={
                                  <div className="text-center p-5">
                                    <Spinner animation="border" />
                                  </div>
                                }
                              >
                                <DataSecurityPage />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/compliance"
                            element={
                              <Suspense
                                fallback={
                                  <div className="text-center p-5">
                                    <Spinner animation="border" />
                                  </div>
                                }
                              >
                                <CompliancePage />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/documentation"
                            element={<Navigate to="/help?tab=documentation" replace />}
                          />
                          <Route
                            path="/api-docs"
                            element={
                              <Suspense
                                fallback={
                                  <div className="text-center p-5">
                                    <Spinner animation="border" />
                                  </div>
                                }
                              >
                                <ApiReferencePage />
                              </Suspense>
                            }
                          />
                          <Route
                            path="/release-notes"
                            element={
                              <Suspense
                                fallback={
                                  <div className="text-center p-5">
                                    <Spinner animation="border" />
                                  </div>
                                }
                              >
                                <ReleaseNotesPage />
                              </Suspense>
                            }
                          />
                        </Route>

                        {/* Protected Routes with Main Layout */}
                        <Route element={<RequireAuth />}>
                          {/* Standard Pages - Use MainLayout (navbar + header + footer) */}
                          <Route
                            element={
                              <Suspense fallback={routeShellFallback}>
                                <MainLayout />
                              </Suspense>
                            }
                          >
                            <Route
                              path="/dashboard"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <Dashboard />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/patients"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <PatientManagement />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/patients/new"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <NewPatientPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/patients/:patientId"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <PatientDetailPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/patients/:patientId/studies/:studyId"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <StudyDetailPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/studies"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <StudiesPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/studies/:studyId"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <StudyDetailPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/reports-and-analytics"
                              element={
                                <Navigate to="/reports/review" replace />
                              }
                            />
                            <Route
                              path="/reports"
                              element={
                                <Navigate to="/reports/review" replace />
                              }
                            />
                            <Route
                              path="/reports/review"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ReportsAndAnalyticsPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/reports/ai-service-ops"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ReportsAndAnalyticsPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/reports/analytics"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ReportsAndAnalyticsPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/reports/cohorts"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ReportsAndAnalyticsPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/reports/outcomes"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ReportsAndAnalyticsPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/reports/workspace"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ReportsAndAnalyticsPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/reports/builder"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ReportsAndAnalyticsPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/reports/*"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ReportsAndAnalyticsPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/eeg/customization"
                              element={
                                <Navigate
                                  to="/eeg/customization/templates"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/eeg/customization/trend-demo"
                              element={
                                <Navigate
                                  to="/eeg/customization/trend-evidence"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/eeg/config/event-catalog"
                              element={
                                <Navigate
                                  to="/eeg/customization/event-catalog"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/eeg/config/trend-templates"
                              element={
                                <Navigate
                                  to="/eeg/customization/trend-templates"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/eeg/config/electrodes"
                              element={
                                <Navigate
                                  to="/eeg/customization/electrodes"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/eeg/config/references"
                              element={
                                <Navigate
                                  to="/eeg/customization/references"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/eeg/config/non-eeg-signals"
                              element={
                                <Navigate
                                  to="/eeg/customization/non-eeg-signals"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/eeg/customization/*"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <CustomizationHubPage />
                                </Suspense>
                              }
                            />
                            {/* Phase 3 (AI/ML) — gated by regulatory phase */}
                            <Route
                              path="/monitoring/tech-qc"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <MonitoringWorkbenchPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/monitoring/mobile-triage"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <MonitoringWorkbenchPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/monitoring/ambulatory"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <MonitoringWorkbenchPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/monitoring/study-screening"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <MonitoringWorkbenchPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/monitoring/neonatal"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <MonitoringWorkbenchPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/monitoring/wall"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <CentralMonitoringWallPanel />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/epilepsy/source-localization"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <SourceLocalizationPanel />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/admin/vendor-support"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <VendorSupportMatrixPanel />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/imaging/co-review"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ImagingCoReviewPanel />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/reports/productivity"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ReportProductivityPanel />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/monitoring/icu-workbench"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <IcuCeegWorkbenchPanel />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/monitoring/icu/:studyId"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ICUMonitoringDashboard />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/monitoring/icu"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ICUMonitoringDashboard />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/monitoring"
                              element={
                                <Navigate to="/monitoring/icu-workbench" replace />
                              }
                            />
                            <Route
                              path="/reports/new"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <NewReportPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/reports/:id"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ReportDetailPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/worklist"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <WorklistPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/appointments/new"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <NewAppointmentPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/appointments"
                              element={
                                <Navigate to="/appointments/upcoming" replace />
                              }
                            />
                            <Route
                              path="/appointment-tabs"
                              element={
                                <Navigate to="/appointments/upcoming" replace />
                              }
                            />
                            <Route
                              path="/appointments/*"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <AppointmentTabsPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/notifications-and-alerts"
                              element={<Navigate to="/alerts" replace />}
                            />
                            <Route
                              path="/alerts"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <NotificationsPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/mobile/notifications"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <MobileNotificationInboxPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/presets"
                              element={
                                <Navigate to="/presets/perspectives" replace />
                              }
                            />
                            <Route
                              path="/presets/perspectives"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <PresetsPage activeTab="perspectives">
                                    <ModernPerspectivesPage />
                                  </PresetsPage>
                                </Suspense>
                              }
                            />
                            <Route
                              path="/presets/montages"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <PresetsPage activeTab="montages">
                                    <MontagesPage />
                                  </PresetsPage>
                                </Suspense>
                              }
                            />
                            <Route
                              path="/telehealth"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <TelehealthPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/remote-assistance"
                              element={<Navigate to="/help?tab=remote-assistance" replace />}
                            />
                            <Route
                              path="/quality/monitoring"
                              element={
                                <Navigate
                                  to="/monitoring/signal-quality"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/monitoring/signal-quality"
                              element={
                                <QualityWorkspaceNav>
                                  <RegulatoryPhaseGate
                                    phase={2}
                                    fallback={
                                      <div className="alert alert-info" role="status">
                                        Signal Quality is unavailable until Regulatory Phase 2 is enabled.
                                      </div>
                                    }
                                  >
                                    <Suspense
                                      fallback={
                                        <div className="text-center p-5">
                                          <Spinner animation="border" />
                                        </div>
                                      }
                                    >
                                      <SignalQualityMonitoringPage />
                                    </Suspense>
                                  </RegulatoryPhaseGate>
                                </QualityWorkspaceNav>
                              }
                            />
                            <Route
                              path="/collaboration/sessions"
                              element={
                                <RegulatoryPhaseGate phase={3}>
                                  <Suspense
                                    fallback={
                                      <div className="text-center p-5">
                                        <Spinner animation="border" />
                                      </div>
                                    }
                                  >
                                    <CollaborationWorkspacePage />
                                  </Suspense>
                                </RegulatoryPhaseGate>
                              }
                            />
                            <Route
                              path="/collaboration"
                              element={
                                <Navigate to="/collaboration/sessions" replace />
                              }
                            />
                            <Route
                              path="/advanced-analytics"
                              element={<Navigate to="/analytics/lab" replace />}
                            />
                            <Route
                              path="/analytics/advanced"
                              element={<Navigate to="/analytics/lab" replace />}
                            />
                            <Route
                              path="/analytics/lab"
                              element={
                                <RegulatoryPhaseGate phase={3}>
                                  <Suspense
                                    fallback={
                                      <div className="text-center p-5">
                                        <Spinner animation="border" />
                                      </div>
                                    }
                                  >
                                    <AdvancedAnalyticsPage />
                                  </Suspense>
                                </RegulatoryPhaseGate>
                              }
                            />
                            <Route
                              path="/montages"
                              element={
                                <Navigate to="/presets/montages" replace />
                              }
                            />
                            <Route
                              path="/montages/editor"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <MontageEditor mode="clinical" />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/montages/editor/:id"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <MontageEditor mode="clinical" />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/perspectives"
                              element={
                                <Navigate to="/presets/perspectives" replace />
                              }
                            />
                            <Route
                              path="/presets/perspectives/new"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <PerspectiveFormPage mode="create" />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/perspectives/new"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <PerspectiveFormPage mode="create" />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/presets/perspectives/:id/edit"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <PerspectiveFormPage mode="edit" />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/perspectives/:id/edit"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <PerspectiveFormPage mode="edit" />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/presets/perspectives/:id/duplicate"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <PerspectiveFormPage mode="duplicate" />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/perspectives/:id/duplicate"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <PerspectiveFormPage mode="duplicate" />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/presets/perspectives/admin"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <PerspectiveAdminPanel userRole="ADMIN" />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/perspectives/admin"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <PerspectiveAdminPanel userRole="ADMIN" />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/presets/perspectives/help"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <PerspectiveHelpPanel />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/perspectives/help"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <PerspectiveHelpPanel />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/sharing/shared-with-me"
                              element={<Navigate to="/shares/inbox" replace />}
                            />
                            <Route
                              path="/sharing/my-shares"
                              element={<Navigate to="/shares/outbox" replace />}
                            />
                            <Route
                              path="/shares"
                              element={<Navigate to="/shares/inbox" replace />}
                            />
                            <Route
                              path="/shares/:shareBox"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <SharesPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/security"
                              element={
                                <Navigate to="/account/security/mfa" replace />
                              }
                            />
                            <Route
                              path="/account/security"
                              element={
                                <Navigate to="/account/security/mfa" replace />
                              }
                            />
                            <Route
                              path="/account/security/*"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <AccountHubNav><AccountSecurityPage /></AccountHubNav>
                                </Suspense>
                              }
                            />
                            <Route
                              path="/admin/security"
                              element={
                                <Navigate
                                  to="/admin/security/access-control"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/admin/security/*"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <AdminSecurityPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/security-and-privacy"
                              element={
                                <Navigate to="/account/security/mfa" replace />
                              }
                            />
                            <Route
                              path="/settings"
                              element={
                                <Navigate
                                  to="/account/preferences/general"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/preferences"
                              element={
                                <Navigate
                                  to="/account/preferences/general"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/account/preferences"
                              element={
                                <Navigate
                                  to="/account/preferences/general"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/account/preferences/support"
                              element={
                                <Navigate to="/help?tab=support" replace />
                              }
                            />
                            <Route
                              path="/account/preferences/*"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <AccountHubNav><SettingsPage /></AccountHubNav>
                                </Suspense>
                              }
                            />
                            <Route
                              path="/my-account"
                              element={
                                <Navigate to="/account/profile" replace />
                              }
                            />
                            <Route
                              path="/account"
                              element={
                                <Navigate to="/account/profile" replace />
                              }
                            />
                            <Route
                              path="/account/profile"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <AccountHubNav><UserDetailsPage /></AccountHubNav>
                                </Suspense>
                              }
                            />
                            {/* Redirect legacy path */}
                            <Route
                              path="/user-details"
                              element={
                                <Navigate to="/account/profile" replace />
                              }
                            />
                            <Route
                              path="/storage-paths"
                              element={
                                <ControlPlaneRouteRedirect
                                  featureName="Storage Path Management"
                                  targetPath="/storage-paths"
                                />
                              }
                            />
                            {/* Redirect MFA setup to Security page with MFA tab */}
                            <Route
                              path="/mfa/setup"
                              element={
                                <Navigate to="/account/security/mfa" replace />
                              }
                            />
                            <Route
                              path="/sessions"
                              element={
                                <Navigate to="/account/sessions" replace />
                              }
                            />
                            <Route
                              path="/account/sessions"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <AccountHubNav><SessionManagementPage /></AccountHubNav>
                                </Suspense>
                              }
                            />
                            <Route
                              path="/admin"
                              element={
                                <Navigate to="/admin/users/active" replace />
                              }
                            />
                            <Route
                              path="/users"
                              element={
                                <Navigate to="/admin/users/active" replace />
                              }
                            />
                            <Route
                              path="/admin/users"
                              element={
                                <Navigate to="/admin/users/active" replace />
                              }
                            />
                            <Route
                              path="/admin/users/*"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <UserManagementPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/admin/sessions"
                              element={
                                <ControlPlaneRouteRedirect
                                  featureName="Admin Session Operations"
                                  targetPath="/admin/sessions"
                                />
                              }
                            />
                            <Route
                              path="/organizations"
                              element={
                                <ControlPlaneRouteRedirect
                                  featureName="Global Organization Management"
                                  targetPath="/organizations"
                                />
                              }
                            />
                            <Route
                              path="/organization-settings"
                              element={
                                <Navigate
                                  to="/admin/organization/general"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/admin/organization"
                              element={
                                <Navigate
                                  to="/admin/organization/general"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/admin/organization/*"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <OrganizationSettingsPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/admin/ehr-connections"
                              element={
                                <Navigate
                                  to="/admin/interoperability/connections"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/admin/interoperability/connections"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <InteroperabilityNav><EHRConnectionsPage /></InteroperabilityNav>
                                </Suspense>
                              }
                            />
                            <Route
                              path="/admin/ehr-sync-activity"
                              element={
                                <Navigate
                                  to="/admin/interoperability/sync-activity"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/admin/interoperability/sync-activity"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <InteroperabilityNav><EHRSyncActivityPage /></InteroperabilityNav>
                                </Suspense>
                              }
                            />
                            <Route
                              path="/admin/roles"
                              element={
                                <ControlPlaneRouteRedirect
                                  featureName="Role Management"
                                  targetPath="/admin/roles"
                                />
                              }
                            />
                            <Route
                              path="/admin/dashboard"
                              element={
                                <ControlPlaneRouteRedirect
                                  featureName="Platform Admin Dashboard"
                                  targetPath="/admin/dashboard"
                                />
                              }
                            />
                            <Route
                              path="/admin/governance"
                              element={
                                <Navigate
                                  to="/admin/governance/compliance"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/admin/icd-codes"
                              element={
                                <Navigate
                                  to="/admin/governance/icd-codes"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/admin/governance/icd-codes"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ICDCodeManagementPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/admin/annotation-types"
                              element={
                                <Navigate
                                  to="/admin/governance/annotations"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/admin/governance/annotations"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <AnnotationTypeManagementPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/admin/priority-workflow"
                              element={
                                <Navigate
                                  to="/admin/governance/workflow-rules/priorities"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/admin/governance/workflow-rules"
                              element={
                                <Navigate
                                  to="/admin/governance/workflow-rules/priorities"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/admin/governance/workflow-rules/*"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <PriorityWorkflowManagementPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/admin/notifications"
                              element={
                                <Navigate
                                  to="/admin/governance/alert-policies"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/admin/document-templates"
                              element={
                                <Navigate
                                  to="/admin/governance/document-templates"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/admin/governance/document-templates"
                              element={
                                <FeatureGate
                                  feature="document_templates_admin"
                                  fallback={
                                    <Navigate to="/dashboard" replace />
                                  }
                                >
                                  <Suspense
                                    fallback={
                                      <div className="text-center p-5">
                                        <Spinner animation="border" />
                                      </div>
                                    }
                                  >
                                    <DocumentTemplateLibraryPage />
                                  </Suspense>
                                </FeatureGate>
                              }
                            />
                            <Route
                              path="/admin/governance/alert-policies"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <NotificationAdminPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/compliance/dashboard"
                              element={
                                <Navigate
                                  to="/admin/governance/compliance"
                                  replace
                                />
                              }
                            />
                            <Route
                              path="/admin/governance/compliance"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ComplianceDashboard />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/help"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <HelpPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/docs"
                              element={
                                <Navigate to="/help?tab=documentation" replace />
                              }
                            />
                            <Route
                              path="/docs/user-guides"
                              element={
                                <Navigate to="/help?tab=documentation" replace />
                              }
                            />
                            <Route
                              path="/docs/User-Guides"
                              element={
                                <Navigate to="/help?tab=documentation" replace />
                              }
                            />
                            <Route
                              path="/docs/user-guides/:guideSlug"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <UserGuidePage />
                                </Suspense>
                              }
                            />
                            {/* Legacy .md paths redirect to /docs/user-guides/:slug */}
                            <Route
                              path="/docs/User-Guides/:docFile"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <UserGuidePage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/help/support"
                              element={<Navigate to="/help/support/tickets" replace />}
                            />
                            <Route
                              path="/help/support/tickets"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <MySupportTicketsPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/help/support/conversations"
                              element={<Navigate to="/help/support/tickets" replace />}
                            />
                            <Route
                              path="/help/support/conversations/:conversationId"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <SupportConversationPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/help/docs"
                              element={<Navigate to="/help?tab=documentation" replace />}
                            />
                            <Route
                              path="/training"
                              element={<Navigate to="/help/training" replace />}
                            />
                            <Route
                              path="/help/training"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <TrainingPortal />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/help/eeg-enablement"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ClinicalEnablementPanel />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/help/release-notes"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ReleaseNotesPage />
                                </Suspense>
                              }
                            />
                            {/* Sprint 12: Deprecated routes - redirect to patient-centric workflow */}
                            <Route
                              path="/eeg-data-visualization"
                              element={
                                <Suspense fallback={routeShellFallback}>
                                  <FeatureDeprecationNotice
                                    featureName="EEG Data Visualization (Standalone)"
                                    deprecationMessage="Access EEG recordings through Patients → Select Patient → Studies → View. This ensures you always have the correct patient context."
                                    redirectPath="/patients"
                                    redirectLabel="Go to Patients"
                                  />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/eeg-integrated"
                              element={
                                <Suspense fallback={routeShellFallback}>
                                  <FeatureDeprecationNotice
                                    featureName="Integrated EEG Viewer"
                                    deprecationMessage="The integrated viewer has been merged into the main viewer. Access via Patients → Select Patient → Studies → View."
                                    redirectPath="/patients"
                                    redirectLabel="Go to Patients"
                                  />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/video-eeg/review/:studyId?"
                              element={
                                <Suspense fallback={routeShellFallback}>
                                  <FeatureDeprecationNotice
                                    featureName="Video-EEG Review (Standalone)"
                                    deprecationMessage="Video-EEG features are now integrated into the main viewer. You can toggle video playback from within the EEG viewer. Access via Patients → Select Patient → Studies → View, then click 'Show Video'."
                                    redirectPath="/patients"
                                    redirectLabel="Go to Patients"
                                  />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/video-eeg"
                              element={
                                <Navigate to="/video-eeg/review" replace />
                              }
                            />
                            <Route
                              path="/qa/dashboard"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <QualityWorkspaceNav>
                                    <QADashboard />
                                  </QualityWorkspaceNav>
                                </Suspense>
                              }
                            />
                            <Route
                              path="/visualization/3d/:studyId"
                              element={
                                <RegulatoryPhaseGate phase={3}>
                                  <Suspense
                                    fallback={
                                      <div className="text-center p-5">
                                        <Spinner animation="border" />
                                      </div>
                                    }
                                  >
                                    <Visualization3DPage />
                                  </Suspense>
                                </RegulatoryPhaseGate>
                              }
                            />
                            <Route
                              path="/visualization/3d"
                              element={
                                <RegulatoryPhaseGate phase={3}>
                                  <Suspense
                                    fallback={
                                      <div className="text-center p-5">
                                        <Spinner animation="border" />
                                      </div>
                                    }
                                  >
                                    <Visualization3DPage />
                                  </Suspense>
                                </RegulatoryPhaseGate>
                              }
                            />
                            <Route
                              path="/qa"
                              element={<Navigate to="/qa/dashboard" replace />}
                            />
                            <Route
                              path="/billing/org-commercial"
                              element={
                                <RequireCommercialBillingAccess>
                                  <BillingManageRedirect />
                                </RequireCommercialBillingAccess>
                              }
                            />
                            <Route
                              path="/admin/billing"
                              element={
                                <RequireCommercialBillingAccess>
                                  <BillingManageRedirect />
                                </RequireCommercialBillingAccess>
                              }
                            />
                            <Route
                              path="/admin/billing/*"
                              element={
                                <RequireCommercialBillingAccess>
                                  <Suspense
                                    fallback={
                                      <div className="text-center p-5">
                                        <Spinner animation="border" />
                                      </div>
                                    }
                                  >
                                    <BillingManagementPage />
                                  </Suspense>
                                </RequireCommercialBillingAccess>
                              }
                            />
                            <Route
                              path="/billing/org-commercial/invoices"
                              element={
                                <RequireCommercialBillingAccess>
                                  <Navigate
                                    to="/admin/billing/payments"
                                    replace
                                  />
                                </RequireCommercialBillingAccess>
                              }
                            />
                            <Route
                              path="/billing/org-commercial/invoices/:invoiceId"
                              element={
                                <RequireCommercialBillingAccess>
                                  <Suspense
                                    fallback={
                                      <div className="text-center p-5">
                                        <Spinner animation="border" />
                                      </div>
                                    }
                                  >
                                    <OrgCommercialInvoicePage />
                                  </Suspense>
                                </RequireCommercialBillingAccess>
                              }
                            />
                            <Route
                              path="/admin/organization/commercial"
                              element={
                                <RequireCommercialBillingAccess>
                                  <BillingManageRedirect />
                                </RequireCommercialBillingAccess>
                              }
                            />
                            <Route
                              path="/billing/manage"
                              element={
                                <RequireCommercialBillingAccess>
                                  <BillingManageRedirect />
                                </RequireCommercialBillingAccess>
                              }
                            />

                            {/* Orphaned dashboards — now routed and accessible via sidebar */}
                            <Route
                              path="/anonymization/dashboard"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <AnonymizationDashboard />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/audit/integrity"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <IntegrityDashboard />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/data/import-export"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <ImportExportDashboard />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/data"
                              element={<Navigate to="/data/import-export" replace />}
                            />
                            <Route
                              path="/medical-coding/dashboard"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <CodingDashboard />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/coding/study/new"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <MedicalCodingWorkspacePage defaultTab="coding" />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/coding/study/:studyId"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <MedicalCodingWorkspacePage defaultTab="coding" />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/coding/worksheets"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <MedicalCodingWorkspacePage defaultTab="worksheet" />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/coding/worksheets/:studyId"
                              element={
                                <Suspense
                                  fallback={
                                    <div className="text-center p-5">
                                      <Spinner animation="border" />
                                    </div>
                                  }
                                >
                                  <MedicalCodingWorkspacePage defaultTab="worksheet" />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/qa/consensus"
                              element={
                                <FeatureGate
                                  feature="QA_DASHBOARD"
                                  fallback={
                                    <Navigate to="/qa/dashboard" replace />
                                  }
                                >
                                  <Suspense
                                    fallback={
                                      <div className="text-center p-5">
                                        <Spinner animation="border" />
                                      </div>
                                    }
                                  >
                                    <QualityWorkspaceNav>
                                      <ConsensusDashboard />
                                    </QualityWorkspaceNav>
                                  </Suspense>
                                </FeatureGate>
                              }
                            />
                          </Route>

                          {/* Video Pop-out — standalone, no layout, no nav */}
                          <Route
                            path="/eeg/video-popout"
                            element={
                              <Suspense
                                fallback={
                                  <div className="video-popout-loading" />
                                }
                              >
                                <VideoPopoutPage />
                              </Suspense>
                            }
                          />

                          {/* EEG Viewer Pages - Use EEGLayout (header + collapsible mini-navbar) */}
                          <Route
                            element={
                              <Suspense fallback={<EEGPlayableShell />}>
                                <EEGLayout />
                              </Suspense>
                            }
                          >
                            <Route
                              path="/viewer"
                              element={
                                <Suspense fallback={<EEGPlayableShell />}>
                                  <ViewerRedirectPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="/eeg/:studyId"
                              element={
                                <Suspense fallback={<EEGPlayableShell />}>
                                  <EEGViewerPage />
                                </Suspense>
                              }
                            />
                          </Route>
                        </Route>

                        <Route
                          path="*"
                          element={
                            <Suspense fallback={routeShellFallback}>
                              <NotFoundPage />
                            </Suspense>
                          }
                        />
                      </Routes>

                      {/* Keyboard Shortcuts Help Modal */}
                      {showShortcuts && (
                        <div
                          className="search-modal-overlay"
                          onClick={() => setShowShortcuts(false)}
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="keyboard-shortcuts-modal"
                        >
                          <div
                            className="bg-white rounded shadow p-4"
                            style={{
                              width: "min(720px, calc(100vw - 2rem))",
                              maxHeight: "calc(100vh - 2rem)",
                              overflow: "auto",
                            }}
                            onClick={(event) => event.stopPropagation()}
                          >
                            <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                              <h2
                                id="keyboard-shortcuts-modal"
                                className="h4 mb-0"
                              >
                                <Trans ns="features" i18nKey="ui.copy.b465751c05fb" /></h2>
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setShowShortcuts(false)}
                                aria-label="Close keyboard shortcuts"
                              >
                                <Trans ns="features" i18nKey="ui.copy.bbfa773e5a63" /></button>
                            </div>
                            <ShortcutHelpPanel shortcuts={shortcuts} />
                          </div>
                        </div>
                      )}

                      {/* Quick Search Modal */}
                      {showQuickSearch && (
                        <div
                          className="search-modal-overlay"
                          onClick={() => setShowQuickSearch(false)}
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="quick-search-title"
                        >
                          <div onClick={(e) => e.stopPropagation()}>
                            <h2 id="quick-search-title" className="sr-only">
                              <Trans ns="features" i18nKey="ui.copy.db7b46304ca2" /></h2>
                            <Suspense fallback={routeShellFallback}>
                              <QuickSearch
                                onClose={() => setShowQuickSearch(false)}
                              />
                            </Suspense>
                          </div>
                        </div>
                      )}
              </ClinicalProviders>
            </UserPreferencesProvider>
          </ThemeProvider>
          </AppDialogProvider>
        </SettingsProvider>
      </ErrorBoundary>
      {/* Performance Monitor - Development Only */}
      {process.env.NODE_ENV === "development" && (
        <Suspense fallback={null}>
          <PerformanceMonitorDashboard />
        </Suspense>
      )}
    </GlobalErrorBoundary>
  );
};

export default App;
